/**
 * Utility for sanitizing user input to prevent Solr injection attacks
 */
export class SolrSanitizer {
  
  /**
   * Special characters that have meaning in Solr query syntax and need to be escaped
   * Includes: + - && || ! ( ) { } [ ] ^ " ~ * ? : \ /
   */
  private static readonly SOLR_SPECIAL_CHARS = /([+\-&|!(){}[\]^"~*?:\\/])/g;

  /**
   * Sanitizes a search term to prevent Solr injection
   * @param searchTerm The user input to sanitize
   * @returns Sanitized and URL-encoded search term
   */
  static sanitizeSearchTerm(searchTerm: string): string {
    if (!searchTerm || typeof searchTerm !== 'string') {
      return '';
    }

    // Trim whitespace
    let sanitized = searchTerm.trim();

    // Escape Solr special characters by prefixing with backslash
    sanitized = sanitized.replace(this.SOLR_SPECIAL_CHARS, '\\$1');

    // URL encode the sanitized string to handle any remaining special characters
    sanitized = encodeURIComponent(sanitized);

    return sanitized;
  }

  /**
   * Sanitizes filter values used in Solr queries
   * @param filterValue The filter value to sanitize
   * @returns Sanitized filter value
   */
  static sanitizeFilterValue(filterValue: string): string {
    if (!filterValue || typeof filterValue !== 'string') {
      return '';
    }

    // Trim and escape special characters
    let sanitized = filterValue.trim();
    sanitized = sanitized.replace(this.SOLR_SPECIAL_CHARS, '\\$1');
    
    return sanitized;
  }

  /**
   * Validates that a search term doesn't contain malicious patterns
   * @param searchTerm The search term to validate
   * @returns True if the search term appears safe
   */
  static isValidSearchTerm(searchTerm: string): boolean {
    if (!searchTerm || typeof searchTerm !== 'string') {
      return false;
    }

    // Check for excessively long input (potential DoS)
    if (searchTerm.length > 500) {
      return false;
    }

    // Check for suspicious patterns that might indicate injection attempts
    const suspiciousPatterns = [
      /\${.*}/,  // Variable interpolation
      /<script/i,  // Script injection
      /javascript:/i,  // JavaScript protocol
      /on\w+\s*=/i  // Event handlers
    ];

    return !suspiciousPatterns.some(pattern => pattern.test(searchTerm));
  }
}
